import React, { useRef, useEffect, useState } from "react";
import SEO from "../components/SEO";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
} from "react-icons/fa";

const Appointment = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const cloudinaryImageUrl =
    "https://res.cloudinary.com/ddvpczzng/image/upload/v1757497447/Untitled_design_1_aqtynv.png";

  useEffect(() => {
    const today = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    if (form.current) {
      form.current.date.value = today;
    }
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .sendForm(
        "service_gr5mw3o",
        "template_l2a0ngg",
        form.current,
        "Na2Bf_RCujJef7MbC"
      )
      .then(
        () => {
          setSubmitSuccess(true);
          form.current.reset();
          setTimeout(() => setSubmitSuccess(false), 5000);
        },
        (error) => {
          alert("Something went wrong. Please try again.");
          console.error("EmailJS Error:", error);
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 bg-[#c5935a]"
      aria-labelledby="contact-info-heading"
    >
      <h2 id="contact-appointment-heading" className="sr-only">
        Schedule a Visit Appointment
      </h2>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className="max-w-6xl mx-auto flex flex-col lg:flex-row bg-[#c5935a] shadow-xl rounded-3xl overflow-hidden"
      >
        {/* Left Image */}
        <div
          className="lg:w-1/2 w-full h-64 sm:h-80 lg:h-auto relative"
          aria-hidden="true"
        >
          <img
            src={cloudinaryImageUrl}
            alt="School campus"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="1600"
            height="1200"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 pointer-events-none" />
        </div>

        {/* Right Form */}
        <div className="lg:w-1/2 w-full p-6 sm:p-8 bg-gray-50">
          <h3 className="text-2xl sm:text-3xl font-bold text-cyan-800 mb-6">
            Schedule a Visit
          </h3>

          {submitSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="mb-4 p-4 bg-green-100 text-green-800 rounded-md"
              role="status"
              aria-live="polite"
            >
              Appointment submitted successfully! We'll contact you soon.
            </motion.div>
          )}

          <form
            ref={form}
            onSubmit={sendEmail}
            aria-label="Appointment Form"
            className="space-y-4 sm:space-y-5"
          >
            <input type="hidden" name="date" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                {
                  type: "text",
                  name: "guardian_name",
                  placeholder: "Parent/Guardian Name",
                  required: true,
                  autoComplete: "name",
                },
                {
                  type: "email",
                  name: "guardian_email",
                  placeholder: "Email Address",
                  required: true,
                  pattern: "[^@\\s]+@[^@\\s]+\\.[^@\\s]+",
                  autoComplete: "email",
                  inputMode: "email",
                },
                {
                  type: "tel",
                  name: "phone",
                  placeholder: "Phone Number",
                  required: true,
                  autoComplete: "tel",
                  inputMode: "tel",
                },
                {
                  type: "text",
                  name: "child_name",
                  placeholder: "Child's Name",
                  required: true,
                  autoComplete: "given-name",
                },
              ].map((field, index) => {
                const id = `contact-appt-${field.name}`;
                return (
                  <div key={index}>
                    <label htmlFor={id} className="sr-only">
                      {field.placeholder}
                    </label>
                    <input
                      id={id}
                      {...field}
                      aria-required={field.required ? "true" : "false"}
                      className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 transition-all duration-200"
                    />
                  </div>
                );
              })}

              <div>
                <label htmlFor="contact-appt-child_age" className="sr-only">
                  Child's Age
                </label>
                <input
                  id="contact-appt-child_age"
                  type="number"
                  name="child_age"
                  placeholder="Child's Age"
                  min="3"
                  max="18"
                  required
                  aria-required="true"
                  inputMode="numeric"
                  className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 transition-all duration-200"
                />
              </div>

              <div>
                <label htmlFor="contact-appt-purpose" className="sr-only">
                  Purpose of Visit
                </label>
                <select
                  id="contact-appt-purpose"
                  name="purpose"
                  required
                  aria-required="true"
                  className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 bg-white appearance-none"
                >
                  <option value="">Purpose of Visit</option>
                  {[
                    "Admission Inquiry",
                    "School Tour",
                    "Meeting with Principal",
                    "Other",
                  ].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="contact-appt-message" className="sr-only">
                Additional Information
              </label>
              <textarea
                id="contact-appt-message"
                name="message"
                placeholder="Additional Information"
                rows="4"
                className="w-full border border-gray-300 px-4 py-2 sm:py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-600 resize-none transition-all duration-200"
              ></textarea>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                aria-disabled={isSubmitting ? "true" : "false"}
                aria-busy={isSubmitting ? "true" : "false"}
                className="w-full max-w-xs px-6 py-2.5 rounded-md font-semibold text-white bg-cyan-600 hover:bg-cyan-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex justify-center items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      role="img"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  "Submit Request"
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </section>
  );
};

const Contact = () => {
  const backgroundImageUrl =
    "https://res.cloudinary.com/ddvpczzng/image/upload/f_auto,q_auto,w_1800/v1757446093/Blog_BG_ylfoqa.png";

  return (
    <div className="min-h-screen bg-[#c5935a]">
      <SEO
        title="Contact Us & Admission Details"
        description="Get in touch with St. Thomas Vidyalayam. Find our address, phone numbers, email, admission criteria, and schedule an appointment."
        keywords="STVMHSS Contact, School Address Chennai, Admission Procedure, Schedule Visit"
        canonicalPath="/contact"
      />

      {/* Screen-reader-only page title for SEO/a11y */}
      <h1 className="sr-only">Contact — STVMHS</h1>

      <div
        className="relative text-white py-20 sm:py-28 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          width: "100%",
        }}
        aria-label="Contact hero"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
          >
            <span className="text-amber-400">Contact</span> Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
            className="font-serif max-w-2xl mx-auto text-lg sm:text-xl md:text-xl"
          >
            We'd love to hear from you. Reach out for any inquiries or
            assistance.
          </motion.p>
        </div>
      </div>

      {/* Main Section */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20"
        aria-labelledby="contact-info-heading"
      >
        <h2 id="contact-info-heading" className="sr-only">
          Contact information and admission details
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 font-serif">
              Contact Information
            </h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-amber-100 p-2 rounded-full mr-4">
                  <FaMapMarkerAlt
                    className="h-5 w-5 text-amber-600"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 font-serif mb-1">
                    Address
                  </h3>
                  <p className="text-gray-600 font-serif">
                    No.10 Dhanalakshmi Nagar, Vanagaram
                  </p>
                  <p className="text-gray-600 font-serif">
                    Chennai, Tamil Nadu 600095
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-blue-100 p-2 rounded-full mr-4">
                  <FaPhone
                    className="h-5 w-5 text-blue-600"
                    aria-hidden="true"
                  />
                </div>
                <div>
  <h3 className="text-lg font-semibold text-gray-800 font-serif mb-1">
    Phone
  </h3>

  <a
    href="tel:+918939649600"
    className="block text-gray-600 font-serif hover:text-blue-600"
  >
    +91 89396 49600
  </a>

  <a
    href="tel:+919087917905"
    className="block text-gray-600 font-serif hover:text-blue-600"
  >
    +91 90879 17905
  </a>
</div>

              </div>

              <div className="flex items-start">
                <div className="bg-red-100 p-2 rounded-full mr-4">
                  <FaEnvelope
                    className="h-5 w-5 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div>
  <h3 className="text-lg font-semibold text-gray-800 font-serif mb-1">
    Email
  </h3>

  <a
    href="mailto:info@stvmhss.in"
    className="block text-gray-600 font-serif hover:text-blue-600"
  >
    info@stvmhss.in
  </a>

  <a
    href="mailto:st0581thomasvid@gmail.com"
    className="block text-gray-600 font-serif hover:text-blue-600"
  >
    st0581thomasvid@gmail.com
  </a>
</div>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 font-serif">
                Connect With Us
              </h3>
              <div className="flex space-x-4">
                {[
                  {
                    icon: <FaFacebook aria-hidden="true" />,
                    color: "hover:text-blue-600",
                    href: "https://www.facebook.com/share/1PeJ6p1fD7/",
                    label: "Facebook",
                  },
                  {
                    icon: <FaInstagram aria-hidden="true" />,
                    color: "hover:text-pink-600",
                    href: "https://www.instagram.com/_stvmhss_/",
                    label: "Instagram",
                  },
                  {
                    icon: <FaWhatsapp aria-hidden="true" />,
                    color: "hover:text-green-600",
                    href: "https://www.whatsapp.com/channel/0029Va9R63NAojZ17JrlGS35",
                    label: "WhatsApp",
                  },
                  {
                    icon: <FaYoutube aria-hidden="true" />,
                    color: "hover:text-red-600",
                    href: "https://youtube.com/@stthomasvidyalayamvioleted7279",
                    label: "YouTube",
                  },
                ].map((social, index) => (
                  <motion.a
                    key={index}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.15 }}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-500 text-2xl transition-colors ${social.color}`}
                    whileHover={{ y: -3 }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Admission Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 font-serif">
              Admission Procedure
            </h2>

            <div className="space-y-6">
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.15 }}
                className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-400"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3 font-serif">
                  Enrollment Criteria
                </h3>
                <ul className="space-y-3 text-gray-700 font-serif">
                  {[
                    "Eligibility: Age 3+ for L.K.G",
                    "Birth & Community Certificate required (except Forward community)",
                    "Transfer Certificate needed (except for L.K.G)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-amber-600 mr-2" aria-hidden="true">
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.15 }}
                className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-400"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-3 font-serif">
                  Required Documents
                </h3>
                <ul className="space-y-3 text-gray-700 font-serif">
                  {[
                    "Birth Certificate",
                    "Previous School Records",
                    "Passport Size Photos (3)",
                    "Transfer Certificate (if applicable)",
                    "Aadhar Card Copy",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-blue-600 mr-2" aria-hidden="true">
                        •
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <div className="flex justify-center pt-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  onClick={() =>
                    window.open(
                      "https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform",
                      "_blank"
                    )
                  }
                  className="w-full sm:w-40 h-12 font-bold rounded-md transition-all duration-200 bg-yellow-400 hover:bg-yellow-500 text-black"
                  aria-label="Open application form in a new tab"
                >
                  Application Form
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Appointment Form Section */}
      <Appointment />

      {/* Google Map */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16 lg:pb-20"
        aria-labelledby="contact-map-heading"
      >
        <h2 id="contact-map-heading" className="sr-only">
          School location map
        </h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.2 }}
          className="rounded-xl overflow-hidden shadow-xl"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.6514183840427!2d80.15025687578257!3d13.05784618726547!2m3!1f0!2f0!0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52617261b0aa17%3A0xef80f22da9e567a3!2sSt%20Thomas%20Vidyalaya!5e0!3m2!1sen!2sin!4v1745074970522!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="School Location"
            className="rounded-xl"
          ></iframe>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
